import { isPwaApp } from '../config/appMode';
import { COMPACT_NAV_MAX_WIDTH } from './compactNav';

/** Same ceiling as compactNav so PWA chrome and hamburger switch together. */
const MOBILE_MAX_WIDTH = COMPACT_NAV_MAX_WIDTH;

function canUseMatchMedia() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/** Installed PWA window only — not a normal browser tab. */
export function isStandalonePwa() {
  if (!canUseMatchMedia()) {
    return false;
  }

  // Chrome/Edge regular tabs always report display-mode: browser
  if (window.matchMedia('(display-mode: browser)').matches) {
    return false;
  }

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.navigator.standalone === true
  );
}

export function isMobileViewport() {
  if (!canUseMatchMedia()) {
    return false;
  }
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/** Wide-screen browser tab with mouse — not the installed PWA window. */
export function isDesktopBrowserTab() {
  if (!canUseMatchMedia() || isStandalonePwa()) {
    return false;
  }

  const isWide = window.matchMedia(`(min-width: ${MOBILE_MAX_WIDTH + 1}px)`).matches;
  if (!isWide) {
    return false;
  }

  if (window.matchMedia('(display-mode: browser)').matches) {
    return true;
  }

  return (
    window.matchMedia('(hover: hover)').matches &&
    window.matchMedia('(pointer: fine)').matches
  );
}

/**
 * PWA chrome — installed app OR mobile-width browser tab only.
 * Desktop browser tabs (wide screen) must match the normal site.
 */
export function shouldShowPwaMobileUi() {
  if (!isPwaApp) {
    return false;
  }
  if (isStandalonePwa()) {
    return true;
  }
  if (isDesktopBrowserTab()) {
    return false;
  }
  return isMobileViewport();
}

export function isIos() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function isIosStandaloneCapable() {
  return isIos() && !isStandalonePwa();
}

export function applyPwaDocumentClass() {
  if (typeof document === 'undefined') {
    return;
  }
  if (isStandalonePwa()) {
    document.documentElement.classList.add('nettles-pwa');
  } else {
    document.documentElement.classList.remove('nettles-pwa');
  }
}

export const PRODUCTION_PERFORMANCE_SECTION_ID = 'production-performance-section';

function findScrollParent(node) {
  let current = node?.parentElement;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/** Scroll PP into view inside the dashboard scroll container (body overflow is hidden). */
export function scrollToProductionPerformance(behavior = 'smooth') {
  const el = document.getElementById(PRODUCTION_PERFORMANCE_SECTION_ID);
  if (!el) {
    return;
  }

  const scrollParent = findScrollParent(el);
  if (!scrollParent) {
    el.scrollIntoView({ behavior, block: 'start' });
    return;
  }

  const headerOffset =
    parseInt(
      window.getComputedStyle(document.documentElement).getPropertyValue('--mobile-header-height'),
      10
    ) || 48;
  const parentRect = scrollParent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const targetTop =
    scrollParent.scrollTop + (elRect.top - parentRect.top) - headerOffset - 12;

  scrollParent.scrollTo({
    top: Math.max(0, targetTop),
    behavior,
  });
}

export const PWA_UI_MEDIA_QUERIES = {
  mobileViewport: `(max-width: ${MOBILE_MAX_WIDTH}px)`,
  desktopViewport: `(min-width: ${MOBILE_MAX_WIDTH + 1}px)`,
  standalone: '(display-mode: standalone)',
  browser: '(display-mode: browser)',
  finePointer: '(pointer: fine)',
  hoverCapable: '(hover: hover)',
};
